/**
 * APX WorkCenter — ChangePasswordModal
 * Stage 2: Uses DB-backed updatePasswordDB via tRPC.
 */

import { useState } from "react";
import { useAuth, updatePasswordDB } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Props {
  onClose: () => void;
}

export default function ChangePasswordModal({ onClose }: Props) {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (next.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      setError("New passwords do not match.");
      return;
    }

    setLoading(true);
    const result = await updatePasswordDB(user!.email, current, next);
    setLoading(false);

    if (!result.success) {
      setError(result.error || "Incorrect current password.");
      return;
    }

    toast.success("Password updated successfully.");
    onClose();
  };

  const inputClass =
    "w-full px-3 py-2.5 rounded-lg text-sm bg-[oklch(0.93_0.008_240)] border border-[oklch(0.82_0.02_240)] text-[oklch(0.08_0.01_240)] placeholder-[oklch(0.45_0.02_240)] focus:outline-none focus:border-[oklch(0.72_0.14_75)] transition-colors";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[oklch(0.08_0.04_240/0.75)] backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal card */}
      <div
        className="relative w-full max-w-sm mx-4 rounded-2xl p-6 shadow-2xl"
        style={{
          background: "oklch(0.97 0.01 220)",
          border: "1px solid oklch(1 0 0 / 8%)",
        }}
      >
        {/* Gold accent line */}
        <div
          className="absolute top-0 left-6 right-6 h-px rounded-full"
          style={{ background: "linear-gradient(90deg, transparent, oklch(0.82 0.01 240), transparent)" }}
        />

        <h3
          className="text-lg font-bold text-[oklch(0.12_0.04_240)] mb-1"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Change Password
        </h3>
        <p className="text-sm text-[oklch(0.35_0.02_240)] mb-5">
          Updating password for <span className="text-[oklch(0.08_0.01_240)]">{user?.email}</span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-semibold uppercase tracking-wider text-[oklch(0.35_0.02_240)] mb-1.5">
              Current Password
            </label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={inputClass}
              placeholder="Enter current password"
              required
              autoComplete="current-password"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold uppercase tracking-wider text-[oklch(0.35_0.02_240)] mb-1.5">
              New Password
            </label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={inputClass}
              placeholder="At least 6 characters"
              required
              autoComplete="new-password"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold uppercase tracking-wider text-[oklch(0.35_0.02_240)] mb-1.5">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputClass}
              placeholder="Repeat new password"
              required
              autoComplete="new-password"
            />
          </div>

          {error && (
            <p className="text-sm text-[oklch(0.08_0.01_240)] bg-[oklch(0.65_0.2_27/10%)] border border-[oklch(0.65_0.2_27/20%)] rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-medium text-[oklch(0.08_0.01_240)] border border-[oklch(1_0_0/10%)] hover:bg-[oklch(1_0_0/4%)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all duration-150 disabled:opacity-50"
              style={{
                background: "linear-gradient(135deg, oklch(0.82 0.01 240), oklch(0.55 0.01 240))",
                color: "oklch(0.12 0.04 240)",
              }}
            >
              {loading ? "Updating…" : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
